import { supabase } from '../lib/supabase';
import { addCsrfHeader } from './csrf';

export const checkPassword = async (password: string): Promise<{
  exposed: boolean;
  count: number;
}> => {
  try {
    const response = await fetch(
      `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/check-password`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          ...addCsrfHeader({})
        },
        body: JSON.stringify({ password })
      }
    );

    if (!response.ok) {
      throw new Error('Failed to check password');
    }

    return await response.json();
  } catch (error) {
    console.error('Password check error:', error);
    // Return false to fail safe (don't block if service is unavailable)
    return { exposed: false, count: 0 };
  }
};

export const getPasswordStrength = async (password: string): Promise<{
  score: number;
  feedback: string;
}> => {
  if (password.length < 6) {
    return {
      score: 0,
      feedback: 'Password is too short'
    };
  }

  // Check if password has been exposed in data breaches
  const { exposed, count } = await checkPassword(password);
  if (exposed) {
    return {
      score: 0,
      feedback: `This password has been exposed in ${count.toLocaleString()} data breaches`
    };
  }

  let score = 0;
  const checks = {
    length: password.length >= 8,
    hasUppercase: /[A-Z]/.test(password),
    hasLowercase: /[a-z]/.test(password),
    hasNumbers: /\d/.test(password),
    hasSpecialChars: /[!@#$%^&*(),.?":{}|<>]/.test(password)
  };

  // Add points for each criteria met
  if (checks.length) score += 1;
  if (checks.hasUppercase) score += 1;
  if (checks.hasLowercase) score += 1;
  if (checks.hasNumbers) score += 1;
  if (checks.hasSpecialChars) score += 1;

  let feedback = '';
  if (score < 2) {
    feedback = 'Consider using a mix of letters, numbers, and symbols';
  } else if (score < 3) {
    feedback = 'Good, but could be stronger';
  } else if (score < 4) {
    feedback = 'Strong password';
  } else {
    feedback = 'Very strong password';
  }

  return { score, feedback };
};